import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { updateVideoName } from "../../Apis/endpoints";
import { deleteS3Path } from "../../Apis/endpoints";
import { deleteVideo } from "../../Apis/endpoints";
import { FaEdit, FaEllipsisV } from "react-icons/fa";

const ResultFeed = ({ results = [] }) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [currentVideoId, setCurrentVideoId] = useState(null);
  const [newVideoName, setNewVideoName] = useState("");
  const [menuOpen, setMenuOpen] = useState(null);
  const menuRef = useRef(null); // Ref to track the menu element

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid Date";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Function to truncate text to a maximum length
  const truncateText = (text, maxLength) => {
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + "...";
    }
    return text;
  };

  // Function to handle the update of the video name
  const handleUpdateVideoName = async () => {
    try {
      await updateVideoName(currentVideoId, newVideoName);
      setIsEditing(false);
      setNewVideoName("");
      window.location.reload();
    } catch (error) {
      console.error("Error updating video name:", error);
    }
  };

  const handleDeleteS3Path = async (videoId) => {
    try {
      await deleteS3Path(videoId);
      setMenuOpen(null);
      window.location.reload();
    } catch (error) {
      console.error("Error deleting S3 path:", error);
    }
  };

  const handleDeleteVideo = async (videoId) => {
    try {
      await deleteVideo(videoId);
      setMenuOpen(null);
      window.location.reload();
    } catch (error) {
      console.error("Error deleting video:", error);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(null);
      }
    };

    // Add event listener when menu is open
    if (menuOpen !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4 p-4">
      {results
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .map((result) => (
          <div
            key={result.id}
            className="relative bg-white border border-gray-200 rounded-xl shadow-md dark:bg-gray-800 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-shadow duration-300"
            style={{ width: "100%" }}
          >
            <div className="relative">
              <video
                className="rounded-t-xl w-full h-40 bg-[#333] object-cover"
              >
                <source
                  src={
                    `https://comski-dev.s3.ap-south-1.amazonaws.com/${result.s3_path}` ||
                    "/default-video.mp4"
                  }
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
              {/* Menu Icon at Top Right */}
              <div className="absolute top-2 right-2">
                <button
                  onClick={() =>
                    setMenuOpen(menuOpen === result.id ? null : result.id)
                  }
                  className="text-white hover:text-gray-200 focus:outline-none bg-gray-800 bg-opacity-50 rounded-full p-1"
                >
                  <FaEllipsisV size={18} />
                </button>
                {menuOpen === result.id && (
                  <div
                    ref={menuRef} // Attach ref to the menu
                    className="absolute right-0 mt-2 w-48 bg-white border border-gray-300 rounded-lg shadow-xl z-10"
                  >
                    {result.s3_path && (
                      <button
                        onClick={() => handleDeleteS3Path(result.video_id)}
                        className="block w-full text-left px-4 py-2 text-sm font-medium text-gray-800 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-200"
                      >
                        Delete Video
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteVideo(result.video_id)}
                      className="block w-full text-left px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors duration-200"
                    >
                      Delete Analysis
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Floating Image with Score */}
            <div className="absolute right-4 top-3/5 transform -translate-y-1/2">
              <svg width="63" height="63" viewBox="0 0 63 63" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g filter="url(#filter0_d_6936_10446)">
                  <path d="M24.2964 1.74295C28.4627 -0.583326 33.5373 -0.583325 37.7036 1.74295C39.27 2.61751 40.9923 3.17713 42.7735 3.39026C47.5115 3.95719 51.6169 6.93992 53.6202 11.2708C54.3733 12.899 55.4378 14.3641 56.7536 15.5836C60.2535 18.8271 61.8216 23.6533 60.8966 28.3346C60.5489 30.0945 60.5489 31.9055 60.8966 33.6654C61.8216 38.3467 60.2535 43.1729 56.7536 46.4164C55.4378 47.6359 54.3733 49.101 53.6202 50.7292C51.6169 55.0601 47.5115 58.0428 42.7735 58.6097C40.9923 58.8229 39.27 59.3825 37.7036 60.257C33.5373 62.5833 28.4627 62.5833 24.2964 60.257C22.7301 59.3825 21.0077 58.8229 19.2265 58.6097C14.4885 58.0428 10.3831 55.0601 8.3798 50.7292C7.62666 49.101 6.5622 47.6359 5.24642 46.4164C1.74653 43.1729 0.178416 38.3467 1.10336 33.6654C1.45109 31.9055 1.45109 30.0945 1.10336 28.3346C0.178416 23.6533 1.74653 18.8271 5.24642 15.5836C6.5622 14.3641 7.62666 12.899 8.3798 11.2708C10.3831 6.93992 14.4885 3.95719 19.2265 3.39026C21.0077 3.17713 22.7301 2.61751 24.2964 1.74295Z" fill="#2563EB" />
                  <path d="M24.7839 2.61607C28.6473 0.458967 33.3527 0.458968 37.2161 2.61607C38.8964 3.55423 40.7439 4.15455 42.6547 4.38318C47.0482 4.90888 50.855 7.67469 52.7126 11.6907C53.5205 13.4373 54.6624 15.0089 56.0738 16.317C59.3192 19.3247 60.7733 23.7999 59.9156 28.1408C59.5426 30.0287 59.5426 31.9713 59.9156 33.8592C60.7733 38.2001 59.3192 42.6753 56.0738 45.683C54.6624 46.9911 53.5205 48.5627 52.7126 50.3093C50.855 54.3253 47.0482 57.0911 42.6547 57.6168C40.7439 57.8455 38.8964 58.4458 37.2161 59.3839C33.3527 61.541 28.6473 61.541 24.7839 59.3839C23.1036 58.4458 21.2561 57.8455 19.3453 57.6168C14.9518 57.0911 11.145 54.3253 9.28741 50.3093C8.4795 48.5627 7.33763 46.9911 5.92616 45.683C2.68079 42.6753 1.22671 38.2001 2.08439 33.8592C2.45742 31.9713 2.45742 30.0287 2.08439 28.1408C1.22671 23.7999 2.68079 19.3247 5.92616 16.317C7.33763 15.0089 8.4795 13.4373 9.28741 11.6907C11.145 7.67469 14.9518 4.90888 19.3453 4.38318C21.2561 4.15455 23.1036 3.55423 24.7839 2.61607Z" stroke="#19154A" stroke-width="2" />
                </g>
                <defs>
                  <filter id="filter0_d_6936_10446" x="0.842773" y="0" width="61.3145" height="63" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                    <feFlood flood-opacity="0" result="BackgroundImageFix" />
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                    <feComposite in2="hardAlpha" operator="out" />
                    <feColorMatrix type="matrix" values="0 0 0 0 0.0980392 0 0 0 0 0.0823529 0 0 0 0 0.290196 0 0 0 1 0" />
                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_6936_10446" />
                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_6936_10446" result="shape" />
                  </filter>
                </defs>
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white font-semibold bg-opacity-50 rounded-full p-2">
                {/* Communication Score */}
                <p className="text-lg">{result.Communication_Score ?? "N/A"}</p>

                {/* Gradient line */}
                <div className="w-10 h-[3px] bg-gradient-to-r from-transparent via-white to-transparent my-0.2"></div>

                {/* Score label */}
                <p className="text-sm">Score</p>
              </div>
            </div>

            <div className="p-4 flex flex-col justify-between h-[40%]">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#002952] dark:text-[#002952]">
                    {result.video_type}
                  </p>
                  <h6 className="text-lg font-semibold text-[#002952] dark:text-white truncate">
                    {truncateText(result.video_name, 10)}
                  </h6>
                </div>
                <button
                  onClick={() => {
                    setCurrentVideoId(result.video_id);
                    setNewVideoName(result.video_name);
                    setIsEditing(true);
                  }}
                  className="text-[#2563EB] dark:text-[#2563EB] hover:text-[#2563EB] text-sm focus:outline-none mt-4"
                >
                  <FaEdit size={18} /> {/* Pencil icon with size */}
                </button>
              </div>

              <div className="flex items-center justify-between mt-3 pt-2 rounded-lg">
                <p className="text-sm text-[#002952] dark:text-[#002952]">
                  {formatDate(result.created_at)}
                </p>
                <button
                  onClick={() => {
                    navigate(`/result/${result.video_id}/${result.mode}`);
                  }}
                  className="text-[#2563EB] dark:text-[#2563EB] hover:text-[#2563EB] text-sm focus:outline-none"
                >
                  View Detail
                </button>
              </div>
            </div>
          </div>
        ))}

      {isEditing && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-4 rounded-lg shadow-lg">
            <h2 className="text-lg font-semibold mb-4">Edit Video Name</h2>
            <input
              type="text"
              value={newVideoName}
              onChange={(e) => setNewVideoName(e.target.value)}
              className="border border-gray-300 rounded-lg p-2 w-full mb-4"
            />
            <div className="flex justify-end">
              <button
                onClick={() => setIsEditing(false)}
                className="text-gray-500 hover:text-gray-700 mr-4"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateVideoName}
                className="bg-blue-500 text-white rounded-lg px-4 py-2"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResultFeed;

// {results.map((result) => (
//  <div
//  key={result.id}
//  className="relative bg-white border border-gray-200 rounded-xl shadow-md dark:bg-gray-800 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-shadow duration-300"
//  style={{ maxWidth: "220px", width: "100%" }}
// >
//  <video
//    className="rounded-t-xl w-full h-40 bg-[#333] object-cover"
//  >
//    <source src={`https://comski.s3.ap-south-1.amazonaws.com/${result.s3_path}` || "/default-video.mp4"} type="video/mp4" />
//    Your browser does not support the video tag.
//  </video>


// {/* Floating Image with Score */}
// <div className="absolute right-4 top-3/5 transform -translate-y-1/2">
//   <img src="/images/flower.svg" alt="Flower" className="w-16 h-16 " />
//   <div className="absolute inset-0 flex flex-col items-center justify-center text-white font-semibold bg-black bg-opacity-50 rounded-full p-2">
//   {/* Increased Communication Score text size */}
//   <p className="text-lg">{result.Communication_Score ?? "N/A"}</p>

//   {/* Gradient line */}
//   <div className="w-10 h-[3px] bg-gradient-to-r from-transparent via-white to-transparent my-0.2"></div>

//   {/* Score label */}
//   <p className="text-sm">Score</p>
// </div>
// </div>
//  <div className="p-4 flex flex-col justify-between h-[40%]">
//    <div>
//      <p className="text-sm text-[#002952] dark:text-[#002952]">{result.video_type}</p>
//      <h6 className="text-lg font-semibold text-[#002952] dark:text-white truncate">
//        {result.video_name}
//      </h6>
//    </div>

//    <div className="flex items-center justify-between mt-3 pt-2 rounded-lg">
//      <p className="text-sm text-[#002952] dark:text-[#002952]">
//        {formatDate(result.created_at)}
//      </p>
//      <button
//        onClick={() => {
//         //  navigate(`/result`, { state: { videoId: result.video_id } });
//         navigate(`/result/${result.video_id}`);
//        }}
//        className="text-[#2563EB] dark:text-[#2563EB] hover:text-[#2563EB] dark:hover:text-[#2563EB] text-sm focus:outline-none"
//      >
//        View Detail
//      </button>
//    </div>
//  </div>
// </div>

// ))}